import React from "react";
import ReactDOM from "react-dom/client";
import { Paper, Popper } from "@mui/material";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import AISparkleIcon from "../../assets/AISparkleIcon";
import { AiComparisonStatus } from "../../Enums.js";
import { deriveTheme } from "./deriveTheme.js";
import "./AiComparisonPopup.css";

let currentInstance = null;

function joinLabels(items) {
  return (Array.isArray(items) ? items : [])
    .map((item) => item?.label || item?.name || item?.value)
    .filter(Boolean)
    .join(", ");
}

function getStatusCopy(comparison) {
  switch (comparison?.status) {
    case AiComparisonStatus.SAME:
      return { label: "Match", tone: "match", title: "User and AI match" };
    case AiComparisonStatus.AMBIGUOUS:
      return comparison?.submitterMatchesAnyCandidate
        ? { label: "AI option", tone: "option", title: "User choice appears in AI options" }
        : { label: "AI unsure", tone: "option", title: "AI has multiple possible choices" };
    case AiComparisonStatus.SUGGESTION:
      return { label: "Suggested", tone: "option", title: "AI suggested a value" };
    case AiComparisonStatus.DIFFERENT:
      return { label: "Differs", tone: "differs", title: "User and AI differ" };
    default:
      return { label: "AI comparison", tone: "neutral", title: "AI comparison" };
  }
}

function formatCandidateMeta(candidate, index) {
  if (typeof candidate?.score === "number") {
    return `${Math.round(candidate.score * 100)}%`;
  }
  return index === 0 ? "Top choice" : `#${index + 1}`;
}

function candidateLabel(candidate) {
  return candidate?.label || candidate?.name || candidate?.value || "";
}

function normalizeComparableValue(value) {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.map(String).sort().join("|");
  return String(value);
}

function getCandidateComparableValue(candidate) {
  return candidate?.value ?? candidate?.raw?.value ?? candidate?.raw?.name ?? candidate?.label ?? candidate?.name ?? "";
}

function getUserCandidateIndex(comparison, candidates) {
  const userValue = normalizeComparableValue(comparison?.submitterValue);
  if (!userValue) return -1;
  return (Array.isArray(candidates) ? candidates : []).findIndex((candidate) => {
    return normalizeComparableValue(getCandidateComparableValue(candidate)) === userValue;
  });
}

function getOutcomeCopy(comparison, userCandidateIndex) {
  if (comparison?.status === AiComparisonStatus.SAME) {
    return {
      title: "Top AI choice selected",
      detail: "User selection matches the AI top choice.",
    };
  }

  if (userCandidateIndex === 0) {
    return {
      title: "Top AI choice selected",
      detail: "User selection matches the AI top choice.",
    };
  }

  if (userCandidateIndex > 0) {
    return {
      title: "Alternate AI choice",
      detail: `User selected AI option #${userCandidateIndex + 1}.`,
    };
  }

  if (comparison?.status === AiComparisonStatus.DIFFERENT) {
    return {
      title: "User and AI differ",
      detail: "User selection is outside the AI ranked choices.",
    };
  }

  return {
    title: getStatusCopy(comparison).title,
    detail: comparison?.status === AiComparisonStatus.SUGGESTION
      ? "AI has suggested an option; user has not selected it."
      : "AI assessed this option field.",
  };
}

function resolvePopupColours(theme) {
  return {
    "--aic-user-colour": theme.userColour,
    "--aic-ai-colour": theme.aiColour,
    "--aic-headline-colour": theme.aiHeadlineColour,
  };
}

function ChoicePanel({ type, icon, label, value, meta, showValue = true, children }) {
  const displayValue = value || "No value";
  return (
    <section className={`ai-comparison-popup__choice ai-comparison-popup__choice--${type}`}>
      <div className="ai-comparison-popup__choice-head">
        <span className="ai-comparison-popup__choice-icon">{icon}</span>
        <span className="ai-comparison-popup__choice-label">{label}</span>
      </div>
      {showValue ? (
        <div className="ai-comparison-popup__selected-option" title={displayValue}>
          <span className="ai-comparison-popup__selected-value">{displayValue}</span>
          {meta ? <span className="ai-comparison-popup__choice-meta">{meta}</span> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

function AiCandidateList({ candidates, fallbackValue, userCandidateIndex }) {
  const candidateList = (Array.isArray(candidates) ? candidates : []).filter(Boolean);
  const displayCandidates = candidateList.length
    ? candidateList.slice(0, 5)
    : [{ label: fallbackValue }];
  if (!displayCandidates.length) return null;

  return (
    <div className="ai-comparison-popup__candidate-list">
      {displayCandidates.map((candidate, index) => (
        <span
          key={`${candidate?.value || candidate?.label || index}`}
          className={`ai-comparison-popup__candidate${index === 0 ? " ai-comparison-popup__candidate--top" : ""}${index === userCandidateIndex ? " ai-comparison-popup__candidate--user-choice" : ""}`}
        >
          <span className="ai-comparison-popup__candidate-rank">
            {formatCandidateMeta(candidate, index)}
          </span>
          <span className="ai-comparison-popup__candidate-name">
            {candidateLabel(candidate)}
          </span>
          {index === userCandidateIndex ? (
            <span className="ai-comparison-popup__candidate-user-tag">
              <PersonRoundedIcon />
              User selected
            </span>
          ) : null}
        </span>
      ))}
    </div>
  );
}

function AiComparisonPopupComponent({ anchorEl, row, fieldConfig, comparison, theme, onClose }) {
  const T = React.useMemo(() => deriveTheme(theme), [theme]);
  if (!comparison?.enabled) return null;

  const status = getStatusCopy(comparison);
  const fieldLabel = fieldConfig?.headerName || fieldConfig?.name || fieldConfig?.field || fieldConfig?.dataSource || "Field";
  const userLabel = comparison?.labels?.submitter || "User";
  const submitterValue = comparison?.submitterLabel || comparison?.labels?.empty || "No value";
  const aiCandidates = Array.isArray(comparison?.aiCandidates) ? comparison.aiCandidates : [];
  const topAiCandidate = aiCandidates[0] || null;
  const userCandidateIndex = getUserCandidateIndex(comparison, aiCandidates);
  const outcomeCopy = getOutcomeCopy(comparison, userCandidateIndex);
  const aiValue =
    comparison?.aiLabel ||
    candidateLabel(topAiCandidate) ||
    joinLabels(aiCandidates) ||
    (comparison?.status === AiComparisonStatus.NO_AI_PICK ? "No clear pick" : "");
  const popupStyle = resolvePopupColours(T);

  return (
      <Popper
        open
        anchorEl={anchorEl}
        placement="left-start"
        strategy="fixed"
        sx={{ zIndex: 1900 }}
        modifiers={[
        { name: "offset", options: { offset: [10, 0] } },
        { name: "flip", options: { fallbackPlacements: ["right-start", "left-end", "right-end", "bottom-start"], padding: 12 } },
        { name: "preventOverflow", options: { boundary: "viewport", padding: 12, altAxis: true, tether: false } },
        ]}
      >
      <ClickAwayListener onClickAway={onClose}>
        <Paper className={`ai-comparison-popup ai-comparison-popup--${status.tone}`} elevation={0} style={popupStyle}>
          <header className="ai-comparison-popup__header">
            <div>
              <h3 className="ai-comparison-popup__title">
                <span className="ai-comparison-popup__title-label">Field:</span>
                <span className="ai-comparison-popup__title-value">{fieldLabel}</span>
              </h3>
              <div className="ai-comparison-popup__eyebrow">AI assessed option field</div>
            </div>
            <div className="ai-comparison-popup__status">
              {status.tone === "match" ? <CheckCircleRoundedIcon /> : null}
              <span>{status.label}</span>
            </div>
          </header>

          <div className="ai-comparison-popup__body">
            <section className="ai-comparison-popup__hero">
              <div className="ai-comparison-popup__hero-copy">
                <div className="ai-comparison-popup__hero-status">{outcomeCopy.title}</div>
                <div className="ai-comparison-popup__hero-sub">{outcomeCopy.detail}</div>
              </div>
              <div className="ai-comparison-popup__hero-circuit" aria-hidden="true">
                <span className="ai-comparison-popup__hero-token ai-comparison-popup__hero-token--user">
                  <PersonRoundedIcon className="ai-comparison-popup__hero-token-svg" />
                </span>
                <span className="ai-comparison-popup__hero-operator">{comparison.status === AiComparisonStatus.SAME ? "=" : "\u2260"}</span>
                <span className="ai-comparison-popup__hero-token ai-comparison-popup__hero-token--ai">
                  <AISparkleIcon className="ai-comparison-popup__hero-token-svg" />
                </span>
              </div>
            </section>

            <div className="ai-comparison-popup__choices">
              <ChoicePanel
                type="ai"
                icon={<AISparkleIcon />}
                label="AI ranked choices"
                value={aiValue}
                showValue={false}
              >
                <AiCandidateList
                  candidates={aiCandidates}
                  fallbackValue={aiValue}
                  userCandidateIndex={userCandidateIndex}
                />
              </ChoicePanel>
              <ChoicePanel
                type="user"
                icon={<PersonRoundedIcon />}
                label={`${userLabel} selection`}
                value={submitterValue}
              />
            </div>
          </div>

          <footer className="ai-comparison-popup__footer">
            <span className="ai-comparison-popup__footer-meta">
              {row?.meta?.trackingNumber ? `Submission ${row.meta.trackingNumber}` : row?.id ? `Row ${row.id}` : ""}
            </span>
            <button
              type="button"
              className="ai-comparison-popup__close-button"
              onClick={onClose}
              aria-label="Close"
            >
              <CloseRoundedIcon />
              <span>Close</span>
            </button>
          </footer>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
}

function open(props) {
  if (currentInstance) {
    currentInstance.close();
  }
  const container = document.createElement("div");
  document.body.appendChild(container);
  const root = ReactDOM.createRoot(container);
  const close = () => {
    root.unmount();
    container.remove();
    currentInstance = null;
  };
  root.render(<AiComparisonPopupComponent {...props} onClose={close} />);
  currentInstance = { close };
}

export const AiComparisonPopup = { open };
